import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { FaIcon } from '../FaIcon';

class ChartMetricSelectorComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            searchText: '',
        };
        this.popupRef = React.createRef();
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside(event) {
        if (this.popupRef.current && !this.popupRef.current.contains(event.target)) {
            this.setState({ isOpen: false, searchText: '' });
        }
    }

    handleSearchChange(event) {
        this.setState({ searchText: event.target.value });
    }

    getFilteredMetrics() {
        const { fieldsData, sourceType, selectedMetricForExclusion, availableMetrics } = this.props;
        const { searchText } = this.state;
        
        if (!fieldsData || !fieldsData.FIELDS_BY_DATA_SOURCE_TYPE || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELDS_GROUP_DATA) {
            return {};
        }

        const { FIELDS_BY_DATA_SOURCE_TYPE, FIELD_DATA_BY_ID, FIELD_SEGMENTS, FIELDS_GROUP_DATA } = fieldsData;
        const availableFieldIds = FIELDS_BY_DATA_SOURCE_TYPE[sourceType] || [];

        // Crear un Set de métricas disponibles para búsqueda rápida
        const availableMetricsSet = availableMetrics ? new Set(availableMetrics) : new Set();

        // Crear el grupo "Métricas Pre-seleccionadas" con las métricas que están en la tabla
        const fieldsByGroup = {
            'selected': [] // Grupo especial para métricas pre-seleccionadas (de la tabla)
        };
        
        // Inicializar los demás grupos
        Object.keys(FIELDS_GROUP_DATA).forEach(groupId => {
            if (groupId !== 'selected') {
                fieldsByGroup[groupId] = [];
            }
        });

        availableFieldIds.forEach(id => {
            const field = FIELD_DATA_BY_ID[id] ? { ...FIELD_DATA_BY_ID[id], id } : null;
            // Incluir métricas e indicadores que tengan depends_on_daterange === true
            const isMetricOrIndicator = field && 
                (field.segment === FIELD_SEGMENTS.METRICS || field.segment === FIELD_SEGMENTS.INDICATORS) && 
                field.group;
            
            // Filtrar solo aquellos con depends_on_daterange === true
            const hasDependsOnDateRange = field && field.depends_on_daterange === true;
            
            if (isMetricOrIndicator && hasDependsOnDateRange) {
                // Filtrar por texto de búsqueda
                const matchesSearch = !searchText || 
                    field.name.toLowerCase().includes(searchText.toLowerCase()) ||
                    id.toLowerCase().includes(searchText.toLowerCase());
                
                // Excluir la métrica ya seleccionada en el otro selector
                const isNotExcluded = !selectedMetricForExclusion || field.id !== selectedMetricForExclusion;
                
                if (matchesSearch && isNotExcluded) {
                    // Si la métrica tiene datos disponibles, agregarla al grupo "Métricas seleccionadas"
                    if (availableMetricsSet.has(field.id)) {
                        fieldsByGroup['selected'].push(field);
                    } else if (fieldsByGroup[field.group]) {
                        // Si no tiene datos, agregarla a su grupo normal
                        fieldsByGroup[field.group].push(field);
                    }
                }
            }
        });

        return fieldsByGroup;
    }

    handleMetricSelect(metricId) {
        const { onSelect } = this.props;
        if (onSelect) {
            onSelect(metricId);
        }
        this.setState({ isOpen: false, searchText: '' });
    }

    getSelectedMetricName() {
        const { selectedMetric, fieldsData } = this.props;
        if (!selectedMetric || !fieldsData || !fieldsData.FIELD_DATA_BY_ID) {
            return null;
        }
        const field = fieldsData.FIELD_DATA_BY_ID[selectedMetric];
        return field ? field.name : selectedMetric;
    }

    render() {
        const { t, selectedMetric, fieldsData } = this.props;
        const { isOpen, searchText } = this.state;
        
        if (!fieldsData) {
            return null;
        }

        const fieldsByGroup = this.getFilteredMetrics();
        const selectedMetricName = this.getSelectedMetricName();
        const hasGroups = Object.values(fieldsByGroup).some(group => group.length > 0);

        // Color para el indicador de la métrica (azul para la primera, naranja para la segunda)
        const colorClass = this.props.metricIndex === 0 ? '-color-blue' : '-color-orange';

        return (
            <div className="chart-metric-selector" ref={this.popupRef}>
                <button
                    type="button"
                    onClick={() => this.setState({ isOpen: !isOpen })}
                    className={`btn chart-metric-selector__button ${selectedMetric ? '-selected' : ''}`}
                >
                    <div
                        className={`chart-metric-selector__indicator ${selectedMetric ? colorClass : ''}`}
                    />
                    <span className={`chart-metric-selector__text ${selectedMetric ? '-selected' : ''}`}>
                        {selectedMetricName || (this.props.metricIndex === 1 ? t('data_explorer.no_metric', 'Ninguna') : t('data_explorer.select_metric', 'Seleccionar métrica'))}
                    </span>
                    <FaIcon iconName="chevron-down" className="chart-metric-selector__chevron" />
                </button>

                {isOpen && (
                    <div className="chart-metric-selector__popup">
                        <div className="chart-metric-selector__search-container">
                            <input
                                type="text"
                                value={searchText}
                                onChange={this.handleSearchChange}
                                placeholder={t('data_explorer.search_metric', 'Buscar métrica...')}
                                className="form-control chart-metric-selector__search-input"
                                autoFocus
                            />
                        </div>
                        <div className="chart-metric-selector__content">
                            {/* Opción "Ninguna" solo para el segundo selector (metricIndex === 1) */}
                            {this.props.metricIndex === 1 && (
                                <div className="chart-metric-selector__group">
                                    <div className="chart-metric-selector__group-fields">
                                        <div
                                            onClick={() => this.handleMetricSelect(null)}
                                            className={`chart-metric-selector__field-item ${!selectedMetric ? '-selected' : ''}`}
                                        >
                                            {t('data_explorer.no_metric', 'Ninguna')}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {hasGroups ? (
                                // Ordenar grupos para que "selected" aparezca primero
                                Object.entries(fieldsByGroup)
                                    .sort(([groupIdA], [groupIdB]) => {
                                        // "selected" siempre primero
                                        if (groupIdA === 'selected') return -1;
                                        if (groupIdB === 'selected') return 1;
                                        return 0;
                                    })
                                    .map(([groupId, groupFields]) => {
                                        if (!groupFields || groupFields.length === 0) return null;
                                        
                                        // Grupo especial "Métricas Pre-seleccionadas"
                                        if (groupId === 'selected') {
                                            return (
                                                <div key={groupId} className="chart-metric-selector__group">
                                                    <div className="chart-metric-selector__group-title">
                                                        {t('data_explorer.pre_selected_metrics', 'Métricas Pre-seleccionadas')}
                                                    </div>
                                                    <div className="chart-metric-selector__group-fields">
                                                        {groupFields.map(field => (
                                                            <div
                                                                key={field.id}
                                                                onClick={() => this.handleMetricSelect(field.id)}
                                                                className="chart-metric-selector__field-item"
                                                            >
                                                                {field.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        
                                        // Grupos normales
                                        const groupData = fieldsData.FIELDS_GROUP_DATA[groupId];
                                        if (!groupData) return null;
                                        
                                        return (
                                            <div key={groupId} className="chart-metric-selector__group">
                                                <div className="chart-metric-selector__group-title">
                                                    {groupData.name}
                                                </div>
                                                <div className="chart-metric-selector__group-fields">
                                                    {groupFields.map(field => (
                                                        <div
                                                            key={field.id}
                                                            onClick={() => this.handleMetricSelect(field.id)}
                                                            className="chart-metric-selector__field-item"
                                                        >
                                                            {field.name}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })
                            ) : (
                                <div className="chart-metric-selector__no-results">
                                    {t('data_explorer.no_metrics_found', 'No se encontraron métricas')}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export const ChartMetricSelector = withTranslation()(ChartMetricSelectorComponent);

